import { useForm } from 'react-hook-form'

import { type ReactNode, useEffect } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { TextInput, SelectInput, SwitchInput } from '@/components/atoms/inputs'

import { CouponModel } from '@/types'

import { CouponFormState, couponFieldsConfig } from './couponFieldsConfig'
import { useEnterSubmit } from '@/hooks/ui'
import { Spinner } from '@/components/ui/spinner'

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="space-y-4 py-4 first:pt-2 last:pb-0">
    <h3 className="text-sm font-semibold text-ink">{title}</h3>
    {children}
  </section>
)

interface CouponDialogProps {
  open: boolean
  mode: 'create' | 'edit' | 'view'
  editCoupon: CouponModel.Coupon | null
  closeDialog: () => void
  onCreate: (data: CouponModel.CouponFormData) => void
  onUpdate: (id: string, data: CouponModel.CouponFormData) => void
  loading?: boolean
  /** Values the assistant prepared, for create mode. */
  prefill?: Partial<CouponFormState>
  /** Shown above the form while reviewing a prepared change. */
  notice?: ReactNode
}

export const CouponDialog = ({
  open,
  mode,
  editCoupon,
  closeDialog,
  onCreate,
  onUpdate,
  loading = false,
  prefill,
  notice,
}: CouponDialogProps) => {
  const form = useForm<CouponFormState>({
    defaultValues: {
      coupon_code: '',
      description: '',
      discount_type: 'percentage',
      discount: 0,
      min_order_amount: 0,
      max_discount_amount: 0,
      usage_period_days: 365,
      usage_limit: 1,
      expiry_date: '',
      status: 'active',
    },
  })

  const {
    handleSubmit,
    watch,
    reset,
    control,
    formState: { errors },
  } = form

  const formValues = watch()
  const { fields } = couponFieldsConfig({ formValues })

  useEffect(() => {
    if ((mode === 'edit' || mode === 'view') && editCoupon) {
      reset({
        coupon_code: editCoupon.coupon_code,
        description: editCoupon.description,
        discount_type: editCoupon.discount_type,
        discount: editCoupon.discount,
        min_order_amount: editCoupon.min_order_amount,
        max_discount_amount: editCoupon.max_discount_amount,
        usage_period_days: editCoupon.usage_period_days,
        usage_limit: editCoupon.usage_limit,
        expiry_date: editCoupon.expiry_date.split('T')[0],
        status: editCoupon.status,
      })
    } else {
      reset({
        coupon_code: '',
        description: '',
        discount_type: 'percentage',
        discount: 0,
        min_order_amount: 0,
        max_discount_amount: 0,
        usage_period_days: 365,
        usage_limit: 1,
        expiry_date: '',
        status: 'active',
        ...prefill,
      })
    }
  }, [mode, editCoupon, reset, prefill])

  const onSubmit = async (data: CouponFormState) => {
    if (mode === 'create') {
      onCreate(data)
    } else {
      if (!editCoupon?._id) return
      onUpdate(editCoupon._id, data)
    }
  }

  useEnterSubmit(handleSubmit(onSubmit), open && mode !== 'view')

  return (
    <Dialog open={open} onOpenChange={closeDialog}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create'
              ? 'Create coupon'
              : mode === 'edit'
                ? 'Edit coupon'
                : 'Coupon details'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Add a new discount code for your customers.'
              : mode === 'edit'
                ? 'Update coupon information.'
                : 'View coupon details.'}
          </DialogDescription>
        </DialogHeader>
        {notice}

        <div className="divide-y divide-border-subtle">
          <Section title="Coupon code">
            <TextInput
              control={control}
              name="coupon_code"
              label="Code"
              placeholder="BOOM50"
              disabled={mode === 'view'}
              rules={fields.coupon_code.rules}
              maxLength={fields.coupon_code.maxLength}
              errors={errors}
              upperCase
            />

            <TextInput
              control={control}
              name="description"
              label="Description"
              placeholder="e.g. 50tk discount"
              disabled={mode === 'view'}
              rules={fields.description.rules}
              errors={errors}
            />
          </Section>

          <Section title="Value">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <SelectInput
                control={control}
                name="discount_type"
                label="Discount type"
                placeholder="Select discount type"
                disabled={mode === 'view'}
                rules={fields.discount_type.rules}
                options={[
                  { label: 'Percentage (%)', value: 'percentage' },
                  { label: 'Fixed amount', value: 'fixed_amount' },
                ]}
              />

              <TextInput
                control={control}
                name="discount"
                label="Discount value"
                placeholder="50"
                type="number"
                disabled={mode === 'view'}
                rules={fields.discount.rules}
                min={fields.discount.min}
                max={fields.discount.max}
                errors={errors}
                valueAsNumber
              />
            </div>
          </Section>

          <Section title="Requirements">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextInput
                control={control}
                name="min_order_amount"
                label="Minimum order amount"
                placeholder="100"
                type="number"
                disabled={mode === 'view'}
                rules={fields.min_order_amount.rules}
                min={fields.min_order_amount.min}
                errors={errors}
                valueAsNumber
              />

              <TextInput
                control={control}
                name="max_discount_amount"
                label="Maximum discount"
                placeholder="50"
                type="number"
                disabled={mode === 'view'}
                rules={fields.max_discount_amount.rules}
                min={fields.max_discount_amount.min}
                errors={errors}
                valueAsNumber
              />
            </div>
          </Section>

          <Section title="Usage limits">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextInput
                control={control}
                name="usage_limit"
                label="Number of uses"
                placeholder="1"
                type="number"
                disabled={mode === 'view'}
                rules={fields.usage_limit.rules}
                min={fields.usage_limit.min}
                errors={errors}
                valueAsNumber
              />

              <TextInput
                control={control}
                name="usage_period_days"
                label="Usage period (days)"
                placeholder="365"
                type="number"
                disabled={mode === 'view'}
                rules={fields.usage_period_days.rules}
                min={fields.usage_period_days.min}
                errors={errors}
                valueAsNumber
              />
            </div>
          </Section>

          <Section title="Active dates">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextInput
                control={control}
                name="expiry_date"
                label="Expiry date"
                type="date"
                disabled={mode === 'view'}
                rules={fields.expiry_date.rules}
                errors={errors}
              />
            </div>

            <SwitchInput
              control={control}
              name="status"
              label="Status"
              description={
                formValues.status === 'active' ? 'Coupon is active.' : 'Coupon is inactive.'
              }
              disabled={mode === 'view'}
              activeValue="active"
              inactiveValue="inactive"
            />
          </Section>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={closeDialog} disabled={loading}>
            {mode === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {mode !== 'view' && (
            <Button type="button" onClick={handleSubmit(onSubmit)} disabled={loading}>
              {loading && <Spinner />}
              {mode === 'create' ? 'Create coupon' : 'Save'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
